// Crab & Cruise > Bus Groups — bus group payment plans, replacing Rachael's spreadsheet (her email
// Oct 27, 2026). Groups, their payment schedules, a log of declines and calls, grades, and the
// morning reminders. Server side: netlify/functions/lib/cnc-bus-groups.js and
// cnc-bus-payments-scheduled.js.

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, X, Trash2, Eye, Bus } from 'lucide-react';
import { neonQuery } from '../services/api';

const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
const money = (v) => {
  const s = '$' + Math.abs(n(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n(v) < 0 ? `(${s})` : s;
};
const day = (s) => {
  if (!s) return '';
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const today = () => new Date().toLocaleDateString('en-CA');

const STATUSES = ['Scheduled', 'Paid', 'Partial', 'Declined', 'Waived'];
const STATUS_COLOR = { Scheduled: 'var(--gray-600)', Paid: '#15803d', Partial: '#b45309', Declined: '#c0392b', Waived: 'var(--gray-500)' };
const LOG_KINDS = ['Declined', 'Emailed', 'Called', 'Note'];
const TICKET_TYPES = ['Full Package', 'Feast Only', 'VIP'];
const GRADES = [
  ['grade_payment', 'Payment reliability'],
  ['grade_communication', 'Ease of communication'],
  ['grade_event', 'Day of the event'],
];

const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-700)', borderBottom: '2px solid var(--gray-200)', whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', borderBottom: '1px solid var(--gray-100)', verticalAlign: 'top', fontSize: 13 };
const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const sub = { fontSize: 12, color: 'var(--gray-600)' };
const label = { display: 'block', fontSize: 12, color: 'var(--gray-600)', marginBottom: 3 };
const pill = (color) => ({ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: '#fff', background: color, marginRight: 4 });

function Stars({ value, onChange }) {
  return (
    <span>
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" onClick={() => onChange(value === i ? null : i)} aria-label={`${i} of 5`}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, padding: '0 2px', color: i <= (value || 0) ? '#DC582A' : 'var(--gray-300)' }}>
          ★
        </button>
      ))}
    </span>
  );
}

function PaymentRow({ p, onSave, onDelete, onPreview }) {
  const [x, setX] = useState(p);
  useEffect(() => setX(p), [p]);
  const dirty = JSON.stringify(x) !== JSON.stringify(p);
  const set = (k) => (e) => setX({ ...x, [k]: e.target.value });
  return (
    <tr>
      <td style={td}><input type="date" className="form-input" value={x.due_date || ''} onChange={set('due_date')} /></td>
      <td style={{ ...td, ...num }}><input type="number" step="0.01" className="form-input" style={{ width: 110, textAlign: 'right' }} value={x.amount} onChange={set('amount')} /></td>
      <td style={td}>
        <select className="form-input" value={x.status} onChange={set('status')} style={{ color: STATUS_COLOR[x.status] }}>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </td>
      <td style={{ ...td, ...num }}>
        {x.status === 'Partial'
          ? <input type="number" step="0.01" className="form-input" style={{ width: 110, textAlign: 'right' }} value={x.paid_amount} onChange={set('paid_amount')} />
          : <span>{x.status === 'Paid' ? money(x.amount) : n(x.paid_amount) ? money(x.paid_amount) : ''}</span>}
      </td>
      <td style={td}>{(x.status === 'Paid' || x.status === 'Partial') ? <input type="date" className="form-input" value={x.paid_on || ''} onChange={set('paid_on')} /> : null}</td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        {dirty ? <button className="btn btn-primary btn-sm" onClick={() => onSave(x)}>Save</button> : null}
        {p.id ? <button className="btn btn-sm" title="Preview the customer reminder" onClick={() => onPreview(p.id)} style={{ marginLeft: 4 }}><Eye size={14} /></button> : null}
        {p.id ? <button className="btn btn-sm" title="Delete this payment" onClick={() => onDelete(p.id)} style={{ marginLeft: 4 }}><Trash2 size={14} /></button> : null}
        {p.reminded_at ? <div style={sub}>Reminded {day(String(p.reminded_at).slice(0, 10))}</div> : null}
      </td>
    </tr>
  );
}

function GroupPanel({ id, onClose, onChanged }) {
  const blank = { name: '', reservation: '', email: '', phone: '', event_date: '', ticket_type: 'Full Package', tickets: '', total: '', notes: '', remind: true };
  const [g, setG] = useState(id ? null : blank);
  const [payments, setPayments] = useState([]);
  const [log, setLog] = useState([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [entry, setEntry] = useState({ kind: 'Called', happened_on: today(), note: '' });
  const [preview, setPreview] = useState(null);

  const load = useCallback(async (gid) => {
    try {
      const r = await neonQuery('cnc_bus_group_get', { id: gid });
      setG(r.group); setPayments(r.payments); setLog(r.log);
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { if (id) load(id); }, [id, load]);

  const run = async (fn, okMsg) => {
    setErr(''); setMsg('');
    try { await fn(); if (okMsg) setMsg(okMsg); onChanged(); } catch (e) { setErr(e.message); }
  };
  const saveGroup = (patch) => run(async () => {
    const r = await neonQuery('cnc_bus_group_save', { ...g, ...patch });
    await load(r.id);
  }, patch ? '' : 'Saved.');
  const lookup = async () => {
    if (!g.reservation) return;
    try {
      const { order } = await neonQuery('cnc_bus_lookup_order', { reservation: g.reservation });
      if (!order) return setMsg('No C&C order with that reservation number. You can still save the group.');
      setG((x) => ({
        ...x,
        name: x.name || `${order.first_name} ${order.last_name}`.trim(),
        email: x.email || order.email || '',
        phone: x.phone || order.phone || '',
        total: x.total || order.total,
      }));
      setMsg(`Found C&C order ${order.id}: ${order.first_name} ${order.last_name}, ${money(order.total)}.`);
    } catch (e) { setErr(e.message); }
  };
  const savePayment = (p) => run(async () => { await neonQuery('cnc_bus_payment_save', { ...p, group_id: g.id }); await load(g.id); });
  const deletePayment = (pid) => window.confirm('Delete this payment from the schedule?') && run(async () => { await neonQuery('cnc_bus_payment_delete', { id: pid }); await load(g.id); });
  const addLog = () => run(async () => { await neonQuery('cnc_bus_log_add', { ...entry, group_id: g.id }); setEntry({ ...entry, note: '' }); await load(g.id); });
  const deleteLog = (lid) => window.confirm('Delete this log entry?') && run(async () => { await neonQuery('cnc_bus_log_delete', { id: lid }); await load(g.id); });
  const deleteGroup = () => window.confirm(`Delete ${g.name} and its whole payment history? This cannot be undone.`) && run(async () => { await neonQuery('cnc_bus_group_delete', { id: g.id }); onClose(); });
  const showPreview = async (pid) => { try { setPreview(await neonQuery('cnc_bus_reminder_preview', { payment_id: pid })); } catch (e) { setErr(e.message); } };

  if (!g) return <div className="card" style={{ padding: 20 }}>{err || 'Loading…'}</div>;
  const set = (k) => (e) => setG({ ...g, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const short = g.id && g.tickets_covered < g.tickets;

  return (
    <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid #DC582A' }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1 }}>{g.id ? g.name : 'New bus group'}</span>
        <button className="btn btn-sm" onClick={onClose} aria-label="Close"><X size={14} /></button>
      </div>
      <div className="card-body">
        {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
        {msg ? <div style={{ color: '#15803d', marginBottom: 8 }}>{msg}</div> : null}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
          <div><span style={label}>Reservation #</span><input className="form-input" value={g.reservation || ''} onChange={set('reservation')} onBlur={lookup} placeholder="Card transaction or C&C order" /></div>
          <div><span style={label}>Group leader</span><input className="form-input" value={g.name || ''} onChange={set('name')} /></div>
          <div><span style={label}>Email</span><input className="form-input" value={g.email || ''} onChange={set('email')} /></div>
          <div><span style={label}>Phone</span><input className="form-input" value={g.phone || ''} onChange={set('phone')} /></div>
          <div><span style={label}>Event date</span><input type="date" className="form-input" value={g.event_date || ''} onChange={set('event_date')} /></div>
          <div><span style={label}>Ticket type</span>
            <input className="form-input" list="bus-ticket-types" value={g.ticket_type || ''} onChange={set('ticket_type')} />
            <datalist id="bus-ticket-types">{TICKET_TYPES.map((t) => <option key={t} value={t} />)}</datalist>
          </div>
          <div><span style={label}># of tickets</span><input type="number" className="form-input" value={g.tickets} onChange={set('tickets')} /></div>
          <div><span style={label}>Order total</span><input type="number" step="0.01" className="form-input" value={g.total} onChange={set('total')} /></div>
        </div>
        <div style={{ marginTop: 10 }}>
          <span style={label}>Notes</span>
          <textarea className="form-input" rows={2} style={{ width: '100%', fontFamily: 'inherit' }} value={g.notes || ''} onChange={set('notes')} />
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}><input type="checkbox" checked={g.remind !== false} onChange={set('remind')} /> Email this group payment reminders</label>
          {g.id ? <label style={{ fontSize: 13 }}><input type="checkbox" checked={!!g.archived} onChange={set('archived')} /> Archived (no alerts)</label> : null}
          <span style={{ flex: 1 }} />
          {g.id ? <button className="btn btn-sm" onClick={deleteGroup} style={{ color: '#c0392b' }}><Trash2 size={14} /> Delete group</button> : null}
          <button className="btn btn-primary" onClick={() => saveGroup()}>{g.id ? 'Save group' : 'Add group'}</button>
        </div>

        {g.id ? (
          <>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', margin: '16px 0 8px', padding: '10px 12px', background: 'var(--gray-50, #f8fafc)', borderRadius: 6 }}>
              <div><div style={sub}>Paid</div><b>{money(g.paid)}</b> of {money(g.total)}</div>
              <div><div style={sub}>Balance</div><b style={{ color: g.balance > 0.004 ? '#b45309' : '#15803d' }}>{money(g.balance)}</b></div>
              <div><div style={sub}>Per ticket</div>{money(g.per_ticket)}</div>
              <div>
                <div style={sub}>Tickets paid for</div>
                <b style={{ color: short ? '#c0392b' : '#15803d' }}>{g.tickets_covered} of {g.tickets}</b>
                {short ? <div style={{ ...sub, color: '#c0392b' }}>If the plan isn't finished, issue only {g.tickets_covered}.</div> : null}
              </div>
            </div>

            <h4 style={{ margin: '14px 0 6px' }}>Payment schedule</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Due</th><th style={{ ...th, ...num }}>Amount</th><th style={th}>Status</th><th style={{ ...th, ...num }}>Paid</th><th style={th}>Paid on</th><th style={th} /></tr></thead>
                <tbody>
                  {payments.map((p) => <PaymentRow key={p.id} p={p} onSave={savePayment} onDelete={deletePayment} onPreview={showPreview} />)}
                  <PaymentRow key={`new-${payments.length}`} p={{ due_date: '', amount: '', status: 'Scheduled', paid_amount: 0, paid_on: '' }} onSave={savePayment} onDelete={() => {}} onPreview={() => {}} />
                </tbody>
                <tfoot><tr>
                  <td style={{ ...td, fontWeight: 700 }}>Total</td>
                  <td style={{ ...td, ...num, fontWeight: 700 }}>{money(payments.reduce((a, p) => a + n(p.amount), 0))}</td>
                  <td style={td} colSpan={4}>{Math.abs(payments.reduce((a, p) => a + n(p.amount), 0) - n(g.total)) > 0.01
                    ? <span style={{ color: '#b45309' }}>Schedule is {money(payments.reduce((a, p) => a + n(p.amount), 0) - n(g.total))} off the order total.</span> : null}</td>
                </tr></tfoot>
              </table>
            </div>
            <div style={sub}>The last row adds a payment. Use it for an added deposit too.</div>

            <h4 style={{ margin: '18px 0 6px' }}>Grades</h4>
            {GRADES.map(([k, l]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 190, fontSize: 13 }}>{l}</span>
                <Stars value={g[k]} onChange={(v) => saveGroup({ [k]: v })} />
              </div>
            ))}

            <h4 style={{ margin: '18px 0 6px' }}>Declines, calls and emails</h4>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 8 }}>
              <select className="form-input" value={entry.kind} onChange={(e) => setEntry({ ...entry, kind: e.target.value })}>{LOG_KINDS.map((k) => <option key={k}>{k}</option>)}</select>
              <input type="date" className="form-input" value={entry.happened_on} onChange={(e) => setEntry({ ...entry, happened_on: e.target.value })} />
              <input className="form-input" style={{ flex: 1, minWidth: 220 }} placeholder="What happened" value={entry.note} onChange={(e) => setEntry({ ...entry, note: e.target.value })} />
              <button className="btn btn-primary btn-sm" onClick={addLog}>Add</button>
            </div>
            {log.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>{log.map((l) => (
                  <tr key={l.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap', width: 110 }}>{day(l.happened_on)}</td>
                    <td style={{ ...td, width: 110 }}><span style={pill(l.kind === 'Declined' ? '#c0392b' : l.kind === 'Called' || l.kind === 'Emailed' ? '#0e7490' : '#64748b')}>{l.kind}</span></td>
                    <td style={td}>{l.note}<div style={sub}>{l.created_by}</div></td>
                    <td style={{ ...td, width: 30 }}><button className="btn btn-sm" onClick={() => deleteLog(l.id)} aria-label="Delete entry"><Trash2 size={12} /></button></td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <div style={sub}>Nothing logged yet.</div>}
          </>
        ) : <div style={{ ...sub, marginTop: 10 }}>Add the group first, then its payment schedule.</div>}

        {preview ? (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setPreview(null)}>
            <div style={{ background: '#fff', borderRadius: 8, maxWidth: 640, width: '100%', padding: 16 }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center' }}><b style={{ flex: 1 }}>Customer reminder preview</b><button className="btn btn-sm" onClick={() => setPreview(null)}><X size={14} /></button></div>
              <div style={sub}>To: {preview.to || '(no email on the group)'}<br />Subject: {preview.subject}</div>
              <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12, marginTop: 8 }} dangerouslySetInnerHTML={{ __html: preview.html }} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function CncBusGroups() {
  const [d, setD] = useState(null);
  const [season, setSeason] = useState('');
  const [open, setOpen] = useState(null); // group id, or 'new'
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await neonQuery('cnc_bus_groups_list', { season });
      setD(r);
      if (!season) setSeason(r.season);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [season]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    const next = { ...d.settings, ...patch };
    if (patch.customer_reminders && !window.confirm(`Start emailing group leaders ${next.remind_days} days before each payment is due? Use the eye button on any payment to see the email first.`)) return;
    try { const s = await neonQuery('cnc_bus_settings_save', next); setD({ ...d, settings: s }); } catch (e) { setErr(e.message); }
  };

  const groups = (d && d.groups) || [];
  const totals = groups.reduce((a, g) => ({ total: a.total + n(g.total), paid: a.paid + n(g.paid), tickets: a.tickets + n(g.tickets) }), { total: 0, paid: 0, tickets: 0 });

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
        <Bus size={24} color="var(--navy)" />
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Bus Groups</h1>
        <span style={{ flex: 1 }} />
        {d ? d.seasons.map((s) => (
          <button key={s} className={`btn btn-sm ${s === season ? 'btn-primary' : ''}`} onClick={() => { setSeason(s); setOpen(null); }}>{s}</button>
        )) : null}
        <button className="btn btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} className={loading ? 'spin' : ''} /></button>
        <button className="btn btn-primary btn-sm" onClick={() => setOpen('new')}><Plus size={14} /> Add group</button>
      </div>
      <p style={{ ...sub, marginTop: 0, marginBottom: 14 }}>Crab &amp; Cruise bus group payment plans. Every morning CPx emails Customer Service the payments to charge that day.</p>
      {err ? <div style={{ color: '#c0392b', marginBottom: 10 }}>{err}</div> : null}

      {d ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
            <label><input type="checkbox" checked={!!d.settings.customer_reminders} onChange={(e) => saveSettings({ customer_reminders: e.target.checked })} /> <b>Email customers a reminder</b></label>
            <label>
              <input type="number" min={1} max={14} className="form-input" style={{ width: 60 }} value={d.settings.remind_days}
                onChange={(e) => setD({ ...d, settings: { ...d.settings, remind_days: e.target.value } })} onBlur={() => saveSettings({})} /> days before each payment is due
            </label>
            <span style={sub}>{d.settings.customer_reminders ? 'On. Sent from customerservice@crabplace.com, replies come back there.' : 'Off. Nothing goes to customers until this is checked.'}</span>
          </div>
        </div>
      ) : null}

      {open ? <GroupPanel key={open} id={open === 'new' ? null : open} onClose={() => setOpen(null)} onChanged={load} /> : null}

      <div className="card">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Group</th><th style={th}>Event</th><th style={th}>Tickets</th>
              <th style={{ ...th, ...num }}>Total</th><th style={{ ...th, ...num }}>Paid</th><th style={{ ...th, ...num }}>Balance</th>
              <th style={th}>Next due</th><th style={th}>Grade</th>
            </tr></thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.id} onClick={() => setOpen(g.id)} style={{ cursor: 'pointer', background: open === g.id ? '#fff7f2' : undefined }}>
                  <td style={td}>
                    <b>{g.name}</b>{g.archived ? <span style={{ ...sub, marginLeft: 6 }}>archived</span> : null}
                    <div style={sub}>{g.reservation}</div>
                    {g.declined ? <span style={pill('#c0392b')}>Declined</span> : null}
                    {g.overdue ? <span style={pill('#b45309')}>{g.overdue} overdue</span> : null}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{day(g.event_date)}</td>
                  <td style={td}>{g.tickets} {g.ticket_type}
                    {g.tickets_covered < g.tickets ? <div style={{ ...sub, color: '#c0392b' }}>{g.tickets_covered} paid for</div> : null}</td>
                  <td style={{ ...td, ...num }}>{money(g.total)}</td>
                  <td style={{ ...td, ...num }}>{money(g.paid)}</td>
                  <td style={{ ...td, ...num, color: g.balance > 0.004 ? '#b45309' : '#15803d' }}>{money(g.balance)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{g.next_due ? <>{day(g.next_due)}<div style={sub}>{money(g.next_amount)}</div></> : <span style={{ color: '#15803d' }}>Paid in full</span>}</td>
                  <td style={td}>{g.grade_avg != null ? `${g.grade_avg} / 5` : <span style={sub}>Not graded</span>}</td>
                </tr>
              ))}
              {!groups.length && d ? <tr><td style={td} colSpan={8}>No bus groups for {season} yet.</td></tr> : null}
            </tbody>
            {groups.length ? (
              <tfoot><tr>
                <td style={{ ...td, fontWeight: 700 }}>{groups.length} groups</td><td style={td} />
                <td style={{ ...td, fontWeight: 700 }}>{totals.tickets}</td>
                <td style={{ ...td, ...num, fontWeight: 700 }}>{money(totals.total)}</td>
                <td style={{ ...td, ...num, fontWeight: 700 }}>{money(totals.paid)}</td>
                <td style={{ ...td, ...num, fontWeight: 700 }}>{money(totals.total - totals.paid)}</td>
                <td style={td} colSpan={2} />
              </tr></tfoot>
            ) : null}
          </table>
        </div>
      </div>
    </div>
  );
}
